import { Trans, t } from '@lingui/macro';
import Placeholder from '@tiptap/extension-placeholder';
import { useEditor } from "@tiptap/react";
import StarterKit from '@tiptap/starter-kit';
import { PlusCircle, ArrowLeft, Paperclip, AlertCircle, Info, MinusCircle, Wand2, X, HelpCircle, AlertTriangle } from "lucide-react";
import { RecursiveCharacterTextSplitter } from 'langchain/text_splitter';
import React, { useEffect, useReducer, useState } from "react";
import { Const } from "../../const/Constants";
import { Soribot, SoribotSimplified } from "../../models/Soribot";
import { SourceDocument } from "../../models/SoribotSource";
import { Fire } from "../../services/FireClient";
import { Util, pojo } from "../../services/Utils/Util";
import { UtilApp } from "../../services/Utils/UtilApp";
import ProfileImg from "../components/ProfileImg";
import TipTapEditor from "../components/TipTapEditor";
import { My } from "../../services/My";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Progress } from "../../components/ui/progress";
import { useToast } from "../../hooks/use-toast";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { Label } from "../../components/ui/label";
import { ScrollArea } from "../../components/ui/scroll-area";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const SourceDocEditor: React.FC<{ bot: Soribot, sd: SourceDocument, onClose: () => void, chunkNr: number }> = ({ bot, sd, onClose, chunkNr = -1 }) => {
  const { toast } = useToast();
  const [title, setTitle] = useState(sd.title ?? "");
  const [mode, setMode] = useState<'create' | 'editing'>(sd.chunks.length == 0 ? 'create' : 'editing');
  const [chunks, setChunks] = useState<string[]>([]);
  const [changed, setChanged] = useState(false);
  const [divider, setDivider] = useState(sd.divider ?? '');
  const [chunkSize, setChunksize] = useState(1000);
  const [inProgress, setInProgress] = useState(false);
  const [scrolledToChunk, setScrolledToChunk] = useState(-1);
  const [editorHtml, setEditorHtml] = useState('');
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);

  // Alert Dialog state
  const [alertConfig, setAlertConfig] = useState<{
    open: boolean;
    header: string;
    message: string;
    onConfirm?: () => void;
    confirmText?: string;
  }>({ open: false, header: '', message: '' });

  const editor = useEditor({
    extensions: [StarterKit, Placeholder.configure({ placeholder: t`Enter text and press Update.` }),],
    content: '', editable: true, onUpdate: () => setChanged(true)
  });

  useEffect(() => {
    setChunks(pojo(sd.chunks ?? []));
    setTitle(sd.title ?? "");

    if (chunkNr > 0) {
      setTimeout(() => {
        scrollToChunk(chunkNr - 1);
      }, 500);
    }
  }, [sd])

  useEffect(() => {
    if (mode === 'create') {
      updateEditorHtml(chunks);
    }
  }, [mode])

  const showAlert = (header: string, message: string, onConfirm?: () => void, confirmText?: string) => {
    setAlertConfig({ open: true, header, message, onConfirm, confirmText });
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      if (changed) {
        cancel();
      } else {
        onClose();
      }
    }
  };

  return (<>
    <Dialog open={true} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[95vw] h-[95vh] flex flex-col p-0 gap-0">
        {mode === 'create' && HeaderCreate()}
        {mode === 'create' && <div className="flex-1 overflow-hidden">{BodyCreateEditor()}</div>}

        {mode === 'editing' && <>
          {HeaderEditing()}
          <div className="flex flex-1 overflow-hidden">
            {/* Left sidebar for chunks list */}
            <div className="w-1/3 border-r flex flex-col min-w-[300px]">
              <div className="p-4 border-b">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold"><Trans>Chunks</Trans> {chunks.length}</span>
                  <Button variant="ghost" size="sm" onClick={() => setMode('create')}>
                    <Wand2 className="w-4 h-4 mr-2" />
                    <Trans>Splitter</Trans>
                  </Button>
                </div>
                {sd.error && <p className="text-red-500 text-sm mb-2">{sd.error}</p>}
                {sd.sourceFileUrl && (
                  <div className="flex items-center text-sm text-blue-600">
                    <Paperclip className="w-4 h-4 mr-1" />
                    <a target="_blank" href={sd.sourceFileUrl} rel="noreferrer" className="truncate flex-1 hover:underline"> {sd.sourceFileName}</a>
                    {sd.sourceDocLoader && <Info className="w-4 h-4 ml-2 cursor-pointer text-gray-400" onClick={() => toast({ description: `Loader: ${sd.sourceDocLoader}` })} />}
                  </div>
                )}
              </div>
              <ScrollArea className="flex-1">
                {MenuChunks()}
              </ScrollArea>
              <div className="p-2 border-t flex justify-between items-center bg-gray-50">
                <small className="text-gray-500">{getDateText()}</small>
                <Button variant="ghost" size="sm" onClick={deleteSource} className="text-red-500 hover:text-red-700 hover:bg-red-50"><Trans>Delete</Trans></Button>
              </div>
            </div>

            {/* Right side for editing chunks */}
            <div className="flex-1 bg-gray-50 overflow-hidden flex flex-col">
              <ScrollArea className="flex-1 p-4">
                {BodyChunkItems()}
              </ScrollArea>
            </div>
          </div>
        </>}
      </DialogContent>
    </Dialog>

    {/* Generic Alert Dialog */}
    <AlertDialog open={alertConfig.open} onOpenChange={(open) => setAlertConfig(prev => ({ ...prev, open }))}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{alertConfig.header}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div dangerouslySetInnerHTML={{ __html: alertConfig.message }} />
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          {alertConfig.onConfirm ? (
            <>
              <AlertDialogCancel><Trans>Cancel</Trans></AlertDialogCancel>
              <AlertDialogAction onClick={alertConfig.onConfirm}>{alertConfig.confirmText || t`OK`}</AlertDialogAction>
            </>
          ) : (
            <AlertDialogAction onClick={() => setAlertConfig(prev => ({ ...prev, open: false }))}><Trans>Close</Trans></AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>
  );

  function BodyChunkItems() {
    return <div className="space-y-4 pb-10">
      {chunks.map((chunk, i) => {
        return <div key={i} className="bg-white rounded-lg shadow-sm border p-4" id={`chunk-${i}`} >
          <div className={`flex items-center justify-between mb-2 p-2 rounded ${scrolledToChunk === i ? 'bg-primary/10' : ''}`} onClick={() => scrollToChunk(i)}>
            <div className="flex items-center cursor-pointer">
              <span className="font-bold mr-2 w-6 h-6 flex items-center justify-center bg-gray-100 rounded-full text-xs">{i + 1}</span>
              <span className="text-xs text-gray-500"><Trans>{chunk.length} chars</Trans></span>
            </div>
            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); deleteChunk(i); }}><MinusCircle className="w-4 h-4 text-gray-500" /></Button>
          </div>
          <Textarea
            value={chunk}
            onChange={e => changeChunk(e.target.value, i)}
            className="min-h-[150px] mb-2 font-mono text-sm"
          />
          <div className="flex justify-center">
            <Button variant="ghost" size="sm" className="w-full" onClick={() => addChunkAfter(i)}>
              <PlusCircle className="w-4 h-4 mr-2" /> <Trans>Add Chunk Below</Trans>
            </Button>
          </div>
        </div>
      })}
    </div>;
  }

  function BodyCreateEditor() {
    return <div className="bg-gray-50 h-full p-4 overflow-y-auto">
      <div className="sourcedoc-textarea bg-white rounded-lg shadow-sm border min-h-[500px] p-4">
        <TipTapEditor editor={editor} editable={canEdit()} content={editorHtml} />
      </div>
    </div>;
  }

  function HeaderEditing() {
    return <div className="border-b p-4 flex flex-col gap-4 bg-white">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {!changed && <Button variant="ghost" size="icon" onClick={onClose}><X className="w-5 h-5" /></Button>}
          {changed && <Button variant="outline" onClick={cancel}><Trans>Cancel</Trans></Button>}
          {BotAvatarSnippet(bot)}
        </div>

        <div className="flex items-center gap-4">
          {saveButton()}
        </div>
      </div>
      <Input
        value={title}
        onChange={e => setTitle(e.target.value)}
        placeholder={t`Title`}
        maxLength={48}
        onKeyDown={e => UtilApp.runOnEnter(e, saveSourceDoc)}
        id='title-input'
        className="font-bold text-lg"
      />
    </div>
  }

  function HeaderCreate() {
    return <div className="border-b p-4 bg-white">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => setMode('editing')}><ArrowLeft className="w-5 h-5" /></Button>
          <span className="font-bold text-lg"><Trans>Text Splitter</Trans></span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={showHelp}><HelpCircle className="w-5 h-5" /></Button>
          <Button onClick={splitToChunks}><Trans>Update</Trans></Button>
        </div>
      </div>
      <div className="flex gap-4">
        <div className="flex-1">
          <Label><Trans>Divider</Trans></Label>
          <Input
            value={divider}
            placeholder={t`E.g. \\n or a pattern`}
            onChange={e => setDivider(e.target.value)}
            onKeyDown={e => UtilApp.runOnEnter(e, splitToChunks)}
          />
        </div>
        <div className="w-32">
          <Label><Trans>Chunk size</Trans></Label>
          <Input
            value={chunkSize}
            placeholder={'1000'}
            onChange={e => setChunksize(e.target.value ? parseInt(e.target.value) : 0)}
            disabled={!!divider}
          />
        </div>
      </div>
    </div>
  }

  function MenuChunks() {
    return <div className="divide-y">
      {chunks.map((chunk, i) => (
        <div
          key={i}
          onClick={() => scrollToChunk(i)}
          className={`p-3 cursor-pointer hover:bg-gray-50 flex gap-3 ${scrolledToChunk === i ? 'bg-primary/10 border-l-4 border-primary' : ''}`}
        >
          <div className="min-w-[2.5rem] text-center">
            <div className="font-bold text-gray-700">{i + 1}</div>
            <small className="text-gray-400 text-xs">{chunk.length}</small>
          </div>
          <div className="flex-1 min-w-0">
            <div className="truncate text-sm text-gray-600 line-clamp-2" dangerouslySetInnerHTML={{ __html: UtilApp.sanitizedHTML(chunk.substring(0, 100)) }} />
          </div>
        </div>
      ))}
    </div>;
  }

  function changeChunk(value: string, i: number) {
    chunks[i] = value;
    setChunks([...chunks]); // Create new array ref
    setChanged(true);
    forceUpdate();
  }

  function deleteChunk(i: number) {
    chunks.splice(i, 1);
    setChunks([...chunks]);
    setChanged(true);
    forceUpdate();
  }

  function addChunkAfter(i: number) {
    chunks.splice(i + 1, 0, '');
    changeChunk('', i + 1);
  }

  async function splitToChunks() {
    const outText = editor?.getText() ?? "";
    let newChunks: string[] = [];
    if (divider && divider != ' ') {
      const sep = divider.replaceAll(`\\n`, '\n');
      const _chunks = outText.split(sep);

      for (let i = 0; i < _chunks.length; i++) {
        let chunkText = _chunks[i];
        if (i !== 0) {
          chunkText = `${sep}${chunkText}`;
        }
        if (chunkText.length > sep.length)
          newChunks.push(chunkText.trim());
      }
    } else {
      const splitter = new RecursiveCharacterTextSplitter({ chunkSize: chunkSize, chunkOverlap: 0, keepSeparator: true, separators: ['\n\n', '\n', '.'] });
      newChunks = await splitter.splitText(outText);
    }

    sd.divider = divider;
    setChanged(true);
    setChunks(newChunks);
    updateEditorHtml(newChunks);

    if (newChunks.some(c => c.length > 3000)) {
      showAlert(t`Warning`, t`Some chunks are too large. Please split them into smaller chunks.`);
    } else {
      showAlert(
        t`Splitting complete`,
        t`Splitting complete. ${newChunks.length} chunks created.`,
        () => setMode('editing'),
        t`Continue`
      );
    }
  }

  async function saveSourceDoc() {
    if (chunks.some(c => c.length > 3000)) {
      showAlert(t`Warning`, t`Some chunks are too large. Please split them into smaller chunks.`);
      return;
    }

    sd.title = title || t`Untitled`;
    sd.chunks = chunks;

    try {
      setInProgress(true);
      await Fire.bot.saveSourceDocAndSync(sd);
      onClose();
    } catch (error: any) {
      sd.grounded = null;
      await Fire.bot.saveSourceObj(sd);
      showAlert(t`Sync Failed`, t`Failed to sync knowledge base. Your changes has been saved but try to sync again later.<br/><br/>Error: ${error.message}<br/>${JSON.stringify(error)}`);
      setChanged(false);
    } finally {
      setInProgress(false);
    }
  }

  function updateEditorHtml(_chunks: string[]) {
    let tempChunks = pojo(_chunks);
    for (let i = 0; i < tempChunks.length; i++) {
      // const index = tempChunks[i].indexOf(CHUNK_HIGHLIGHT_START);
      const highlights = Util.getStringsBetweenPattern(tempChunks[i], Const.CHUNK_HIGHLIGHT_START, Const.CHUNK_HIGHLIGHT_END);
      highlights.forEach(h => {
        tempChunks[i] = tempChunks[i].replaceAll(h, `<code>${h}</code>`);
      });
    }
    const htmlText = tempChunks.join('<hr>').replaceAll('\n', '<br>');
    setEditorHtml(htmlText);
  }

  function deleteSource() {
    showAlert(
      t`Delete memo`,
      t`Are you sure you want to delete this memo?`,
      () => {
        Fire.bot.deleteSource(sd).then(() => {
          toast({ description: t`Source is deleted` })
          onClose();
        }).catch((err: any) => {
          toast({ variant: "destructive", description: err.message })
        });
      },
      t`Delete`
    );
  }

  function cancel() {
    showAlert(
      t`Cancel save?`,
      t`Do you want to close without saving any changes?`,
      () => onClose(),
      t`Yes, close without saving`
    );
  }

  function scrollToChunk(i: number) {
    const el = document.getElementById(`chunk-${i}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setScrolledToChunk(i);
  }

  function getDateText() {
    if (sd.updated && sd.created.toString() != sd.updated.toString())
      return t`Changed ${UtilApp.dateStrToTimeAndDay(sd.updated, My.user.lang)}`;
    else
      return t`Created ${UtilApp.dateStrToTimeAndDay(sd.created, My.user.lang)}`;
  }

  function canEdit() {
    return true;
  }

  function showHelp() {
    showAlert(
      t`About the text splitting`,
      t`Soribot knowledge consist of small text chunks. In this view, you can split large text into chunks while editing the text.<br/><br/>To control the splitting strategy you have the option to use Divider or Chunk size. Divider patterns will split the chunks at each occurance while Chunk size will give you approximate chunk sizes.<br/><br/>If you'd like to optimize the chunk retrieval search by setting the searchable text you can use [[[ Search able text ]]].`
    );
  }

  function saveButton() {
    const encoder = new TextEncoder();
    let totalSize = 0;
    chunks.forEach(c => totalSize += encoder.encode(c).length);
    const maxSize = 1024000; // 1MB
    const isTooLarge = totalSize > maxSize;

    if (inProgress)
      return null;
    else
      return <div className="flex items-center gap-4">
        <div className="flex flex-col items-end text-xs">
          {isTooLarge && <AlertTriangle className="w-4 h-4 text-red-500 cursor-pointer mb-1" onClick={() => showAlert(t`Error`, t`The text content is too large to save. Remove some text in order to save.`)} />}
          <span className={isTooLarge ? 'text-red-500' : 'text-gray-500'}>{(totalSize / 1000).toFixed(0)}/1024 KB</span>
          <Progress value={(totalSize / maxSize) * 100} className={`w-28 h-2 mt-1 ${isTooLarge ? "bg-red-100" : ""}`} />
        </div>
        <Button disabled={isTooLarge || mode !== 'editing'} onClick={saveSourceDoc} ><Trans>Save and Sync</Trans></Button>
      </div>
  }
}

export default SourceDocEditor;

export function BotAvatarSnippet(bot: Soribot | SoribotSimplified) {
  return <div className="flex items-center gap-2">
    <Avatar className="w-8 h-8">
      <AvatarImage src={bot.imageUrl || '/assets/img/ai-soribot.png'} />
      <AvatarFallback>{bot.name.substring(0, 1)}</AvatarFallback>
    </Avatar>
    <strong>{bot.name}</strong>
  </div>;
}
